import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDUSTRIES = [
    "Technology",
    "Healthcare",
    "Higher Education",
    "K-12 Education",
    "Financial Services",
    "Professional Services / Consulting",
    "Manufacturing",
    "Retail / E-commerce",
    "Hospitality",
    "Government / Public Sector",
    "Non-Profit",
    "Media & Communications",
    "Energy & Utilities",
    "Real Estate",
    "Other",
];

const ORG_SIZES = [
    "1 – 10",
    "11 – 50",
    "51 – 200",
    "201 – 500",
    "501 – 1,000",
    "1,001 – 5,000",
    "5,000+",
];

const LEAD_FIELDS = [
    { key: "first_name", label: "First Name", placeholder: "Jordan", type: "text", required: true },
    { key: "last_name", label: "Last Name", placeholder: "Reyes", type: "text", required: true },
    { key: "email", label: "Work Email", placeholder: "name@organization.com", type: "email", required: true },
    { key: "organization_name", label: "Organization Name", placeholder: "Acme, Inc.", type: "text", required: true },
    { key: "job_title", label: "Job Title", placeholder: "Chief Executive Officer", type: "text", required: true },
];

const emptyLead = {
    first_name: "",
    last_name: "",
    email: "",
    organization_name: "",
    job_title: "",
    industry: "",
    organization_size: "",
};

const Snapshot = () => {
    const navigate = useNavigate();
    const [config, setConfig] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState(0);
    const [lead, setLead] = useState(emptyLead);
    const [answers, setAnswers] = useState({});
    const [touched, setTouched] = useState({});
    const [hpField, setHpField] = useState("");

    useEffect(() => {
        axios
            .get(`${API}/snapshot/config`)
            .then((res) => setConfig(res.data))
            .catch(() => toast.error("Could not load the assessment. Please refresh."))
            .finally(() => setLoadingConfig(false));
    }, []);

    const pillarPages = useMemo(() => {
        if (!config) return [];
        return config.pillars.map((p) => ({
            ...p,
            items: config.questions.filter((q) => q.pillar === p.slug),
        }));
    }, [config]);

    const totalSteps = pillarPages.length + 2; // lead + N pillars + review
    const progressPct = Math.round(((step + 1) / totalSteps) * 100);

    // ---- Validation helpers ----
    const leadValid = useMemo(() => {
        if (!lead.first_name.trim()) return false;
        if (!lead.last_name.trim()) return false;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim())) return false;
        if (!lead.organization_name.trim()) return false;
        if (!lead.job_title.trim()) return false;
        if (!lead.industry) return false;
        if (!lead.organization_size) return false;
        return true;
    }, [lead]);

    const currentPillar = step >= 1 && step <= pillarPages.length ? pillarPages[step - 1] : null;
    const currentPillarComplete = useMemo(() => {
        if (!currentPillar) return true;
        return currentPillar.items.every((q) => !!answers[q.id]);
    }, [currentPillar, answers]);

    const allAnswered = useMemo(() => {
        if (!config) return false;
        return config.questions.every((q) => !!answers[q.id]);
    }, [config, answers]);

    // ---- Handlers ----
    const setLeadField = (k, v) => setLead((prev) => ({ ...prev, [k]: v }));
    const setAnswer = (qid, val) => setAnswers((prev) => ({ ...prev, [qid]: val }));

    const goNext = () => {
        if (step === 0) {
            setTouched({ ...LEAD_FIELDS.reduce((a, f) => ({ ...a, [f.key]: true }), {}), industry: true, organization_size: true });
            if (!leadValid) {
                toast.error("Please complete all fields to continue.");
                return;
            }
        }
        if (currentPillar && !currentPillarComplete) {
            toast.error(`Please answer all 4 questions for ${currentPillar.name}.`);
            return;
        }
        setStep((s) => Math.min(s + 1, totalSteps - 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const goBack = () => {
        setStep((s) => Math.max(s - 1, 0));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const submit = async () => {
        if (!allAnswered) {
            toast.error("Please answer every question before submitting.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/snapshot`, { lead, answers, hp_field: hpField });
            toast.success("Your HUMAN+ Executive Report is ready.");
            const token = res.data.access_token;
            const qs = token ? `?t=${encodeURIComponent(token)}` : "";
            navigate(`/report/${res.data.snapshot_id}${qs}`);
        } catch (err) {
            const msg =
                err?.response?.data?.detail?.message ||
                err?.response?.data?.detail ||
                "Submission failed. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    // ---- Render ----
    if (loadingConfig) {
        return (
            <div className="ih-container py-32 flex items-center justify-center">
                <Loader2 className="animate-spin text-indigo_brand" size={28} />
            </div>
        );
    }

    return (
        <div data-testid="snapshot-page" className="bg-surface min-h-screen pb-24">
            <div style={{ display: "none" }} aria-hidden="true">
                <input
                    type="text"
                    name="hp_field"
                    tabIndex={-1}
                    autoComplete="off"
                    value={hpField}
                    onChange={(e) => setHpField(e.target.value)}
                />
            </div>
            {/* Header */}
            <section className="bg-indigo_brand text-white">
                <div className="ih-container py-12 md:py-16">
                    <p className="ih-eyebrow" style={{ color: "#08C020" }}>
                        Free · 8–10 Minutes · Personalized Report
                    </p>
                    <h1 className="mt-3 font-montserrat font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
                        HUMAN+ Snapshot<span className="text-green_brand">™</span>
                    </h1>
                    <p className="mt-4 font-opensans text-white/75 text-base sm:text-lg max-w-2xl">
                        Assess your organization across the six HUMAN+ pillars.
                        Receive a personalized Executive Report at the end.
                    </p>
                </div>
            </section>

            {/* Progress bar */}
            <div className="sticky top-20 z-30 bg-surface/90 backdrop-blur-md border-b border-surface-border/60">
                <div className="ih-container py-4">
                    <div className="flex items-center justify-between mb-2 gap-4">
                        <p
                            data-testid="snapshot-step-label"
                            className="font-poppins font-medium text-xs uppercase tracking-[0.16em] text-indigo_brand"
                        >
                            {step === 0
                                ? "Step 1 · About You"
                                : step <= pillarPages.length
                                ? `Step ${step + 1} · Pillar ${currentPillar?.number} · ${currentPillar?.name}`
                                : `Step ${totalSteps} · Review & Submit`}
                        </p>
                        <p
                            data-testid="snapshot-progress-pct"
                            className="font-poppins font-medium text-xs text-[#4A5568]"
                        >
                            {progressPct}% complete
                        </p>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-surface-border/60 overflow-hidden">
                        <div
                            data-testid="snapshot-progress-bar"
                            className="h-full bg-green_brand transition-all duration-500 ease-out"
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Card */}
            <div className="ih-container mt-10">
                <div
                    className="ih-card p-8 md:p-12 shadow-medium animate-fade-up"
                    data-testid="snapshot-step-card"
                >
                    {step === 0 && (
                        <div data-testid="snapshot-lead-step">
                            <h2 className="font-montserrat font-bold text-indigo_brand text-2xl sm:text-3xl">
                                Tell us about you & your organization
                            </h2>
                            <p className="mt-3 font-opensans text-[#4A5568] text-sm sm:text-base max-w-2xl">
                                Your information stays private and is used only to
                                personalize your HUMAN+ Executive Report.
                            </p>

                            <div className="mt-8 grid gap-5 sm:grid-cols-2">
                                {LEAD_FIELDS.map((f) => (
                                    <div key={f.key} className={f.key === "organization_name" || f.key === "job_title" ? "sm:col-span-2" : ""}>
                                        <Label
                                            htmlFor={f.key}
                                            className="font-raleway font-semibold text-indigo_brand text-sm"
                                        >
                                            {f.label} {f.required && <span className="text-green_brand">*</span>}
                                        </Label>
                                        <Input
                                            id={f.key}
                                            data-testid={`snapshot-lead-${f.key}`}
                                            type={f.type}
                                            placeholder={f.placeholder}
                                            value={lead[f.key]}
                                            onBlur={() => setTouched((t) => ({ ...t, [f.key]: true }))}
                                            onChange={(e) => setLeadField(f.key, e.target.value)}
                                            className="mt-2 h-12 font-opensans border-surface-border/70 focus-visible:ring-green_brand"
                                        />
                                    </div>
                                ))}

                                <div>
                                    <Label className="font-raleway font-semibold text-indigo_brand text-sm">
                                        Industry <span className="text-green_brand">*</span>
                                    </Label>
                                    <Select
                                        value={lead.industry}
                                        onValueChange={(v) => setLeadField("industry", v)}
                                    >
                                        <SelectTrigger
                                            data-testid="snapshot-lead-industry"
                                            className="mt-2 h-12 font-opensans border-surface-border/70"
                                        >
                                            <SelectValue placeholder="Select industry" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {INDUSTRIES.map((i) => (
                                                <SelectItem
                                                    key={i}
                                                    value={i}
                                                    data-testid={`snapshot-industry-${i}`}
                                                >
                                                    {i}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label className="font-raleway font-semibold text-indigo_brand text-sm">
                                        Organization Size <span className="text-green_brand">*</span>
                                    </Label>
                                    <Select
                                        value={lead.organization_size}
                                        onValueChange={(v) => setLeadField("organization_size", v)}
                                    >
                                        <SelectTrigger
                                            data-testid="snapshot-lead-organization_size"
                                            className="mt-2 h-12 font-opensans border-surface-border/70"
                                        >
                                            <SelectValue placeholder="Select size" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ORG_SIZES.map((s) => (
                                                <SelectItem
                                                    key={s}
                                                    value={s}
                                                    data-testid={`snapshot-size-${s}`}
                                                >
                                                    {s}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <p className="mt-8 inline-flex items-center gap-2 font-opensans text-xs text-[#4A5568]">
                                <Lock size={12} className="text-green_brand" />
                                Your responses are confidential and used only for your report.
                            </p>
                        </div>
                    )}

                    {step >= 1 && step <= pillarPages.length && currentPillar && (
                        <div data-testid={`snapshot-pillar-${currentPillar.slug}`}>
                            <p className="font-poppins font-medium text-green_brand text-[11px] tracking-[0.2em]">
                                PILLAR {currentPillar.number}
                            </p>
                            <h2 className="mt-1 font-montserrat font-bold text-indigo_brand text-2xl sm:text-3xl">
                                {currentPillar.name}
                            </h2>
                            <p className="mt-3 font-opensans text-[#4A5568] text-sm sm:text-base max-w-2xl">
                                Rate how strongly each statement reflects your
                                organization today.
                            </p>

                            <div className="mt-10 space-y-8">
                                {currentPillar.items.map((q, idx) => (
                                    <LikertItem
                                        key={q.id}
                                        index={idx}
                                        question={q}
                                        value={answers[q.id]}
                                        onChange={(v) => setAnswer(q.id, v)}
                                        labels={config.likert}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {step === totalSteps - 1 && (
                        <div data-testid="snapshot-review-step">
                            <h2 className="font-montserrat font-bold text-indigo_brand text-2xl sm:text-3xl">
                                Ready to generate your HUMAN+ Executive Report
                            </h2>
                            <p className="mt-3 font-opensans text-[#4A5568] text-sm sm:text-base max-w-2xl">
                                Review your responses below, then submit. Your personalized report opens on the next screen.
                            </p>

                            <div className="mt-8 grid gap-4 sm:grid-cols-2 ih-card !shadow-none p-6 bg-surface">
                                {Object.entries({
                                    Name: `${lead.first_name} ${lead.last_name}`,
                                    Email: lead.email,
                                    Organization: lead.organization_name,
                                    "Job Title": lead.job_title,
                                    Industry: lead.industry,
                                    "Org Size": lead.organization_size,
                                }).map(([k, v]) => (
                                    <div key={k}>
                                        <p className="font-poppins text-[10px] uppercase tracking-[0.18em] text-[#4A5568]">{k}</p>
                                        <p className="font-raleway font-semibold text-indigo_brand text-sm mt-1">{v}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-8">
                                <p className="font-raleway font-semibold text-indigo_brand text-sm uppercase tracking-[0.16em]">
                                    Pillar responses
                                </p>
                                <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                    {pillarPages.map((p) => {
                                        const total = p.items.length;
                                        const done = p.items.filter((q) => !!answers[q.id]).length;
                                        return (
                                            <li
                                                key={p.slug}
                                                className="flex items-center gap-2 font-opensans text-sm text-indigo_brand"
                                            >
                                                <Check
                                                    size={15}
                                                    className={done === total ? "text-green_brand" : "text-surface-border"}
                                                />
                                                {p.name} <span className="text-[#4A5568] text-xs">({done}/{total})</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Step nav */}
                    <div className="mt-12 pt-8 border-t border-surface-border/60 flex flex-col sm:flex-row gap-3 sm:justify-between">
                        <button
                            type="button"
                            onClick={goBack}
                            disabled={step === 0 || submitting}
                            data-testid="snapshot-back"
                            className="ih-btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <ArrowLeft size={16} />
                            Back
                        </button>

                        {step < totalSteps - 1 ? (
                            <button
                                type="button"
                                onClick={goNext}
                                data-testid="snapshot-next"
                                className="ih-btn-primary"
                            >
                                {step === 0 ? "Begin Assessment" : "Next"}
                                <ArrowRight size={16} />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={submit}
                                disabled={submitting || !allAnswered}
                                data-testid="snapshot-submit"
                                className="ih-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="animate-spin" size={16} />
                                        Generating Report…
                                    </>
                                ) : (
                                    <>
                                        Generate My Executive Report
                                        <ArrowRight size={16} />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ----- Likert row -----
const LikertItem = ({ index, question, value, onChange, labels }) => {
    return (
        <div data-testid={`snapshot-question-${question.id}`}>
            <div className="flex items-start gap-3">
                <span className="font-poppins font-medium text-green_brand text-xs tracking-[0.16em] mt-1">
                    Q{index + 1}
                </span>
                <p className="font-raleway font-semibold text-indigo_brand text-base sm:text-lg leading-snug">
                    {question.text}
                </p>
            </div>
            <div className="mt-4 grid grid-cols-5 gap-2 sm:gap-3">
                {labels.map((l) => {
                    const selected = value === l.value;
                    return (
                        <button
                            key={l.value}
                            type="button"
                            onClick={() => onChange(l.value)}
                            data-testid={`snapshot-answer-${question.id}-${l.value}`}
                            className={`group rounded-xl border-[1.5px] py-3 sm:py-4 px-2 text-center transition-all duration-200 ${
                                selected
                                    ? "border-green_brand bg-green_brand/8 shadow-[0_8px_24px_rgba(8,192,32,0.18)]"
                                    : "border-surface-border/80 hover:border-indigo_brand/40 hover:bg-surface"
                            }`}
                        >
                            <p
                                className={`font-montserrat font-bold text-lg sm:text-xl ${
                                    selected ? "text-green_brand" : "text-indigo_brand"
                                }`}
                            >
                                {l.value}
                            </p>
                            <p
                                className={`mt-1 font-poppins text-[9.5px] sm:text-[10px] uppercase tracking-[0.14em] leading-tight ${
                                    selected ? "text-indigo_brand" : "text-[#4A5568]"
                                }`}
                            >
                                {l.short}
                            </p>
                        </button>
                    );
                })}
            </div>
            <div className="hidden sm:flex justify-between mt-2 px-1 font-opensans text-[11px] text-[#4A5568]">
                <span>Strongly Disagree</span>
                <span>Strongly Agree</span>
            </div>
        </div>
    );
};

export default Snapshot;
